import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFetch, useDebounced } from "@/hooks/useFetch";
import { useStaff } from "@/context/StaffAuth";
import { api, media } from "@/lib/api";
import { dateTime, money, titleCase } from "@/lib/format";
import { DemoBadge, EmptyState, ErrorState, PageHeader, Pager, StatusBadge, StockPill, TableSkeleton, Thumb, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";
import { cn } from "@/lib/utils";

const STATUSES = [
  ["", "All"],
  ["draft", "Drafts"],
  ["published", "Published"],
  ["archived", "Archived"],
];

export default function AdminProducts() {
  const { can } = useStaff();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const dq = useDebounced(q, 300);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    setQ(params.get("q") || "");
  }, [params]);

  useEffect(() => {
    api.get("/admin/brands", { params: { page_size: 500, status: "" } }).then((r) => setBrands(r.data.items || [])).catch(() => {});
  }, []);

  const query = useMemo(
    () => ({
      q: dq,
      status: params.get("status") || "",
      brand_id: params.get("brand_id") || "",
      gender: params.get("gender") || "",
      stock: params.get("stock") || "",
      sort: params.get("sort") || "updated",
      page: Number(params.get("page") || 1),
      page_size: 25,
    }),
    [dq, params]
  );
  const { data, loading, error, reload } = useFetch("/admin/products", query);

  const setParam = (k, v) => {
    const n = new URLSearchParams(params);
    if (!v) n.delete(k); else n.set(k, v);
    if (k !== "page") n.delete("page");
    setParams(n, { replace: true });
  };
  const hasFilters = ["q", "status", "brand_id", "gender", "stock"].some((k) => params.get(k));
  const counts = data?.status_counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-products-page">
      <PageHeader
        eyebrow="Catalog"
        title="Products"
        description="Every style in the assortment with its variants, imagery and publishing state. Only published products with an active brand and category appear on the storefront."
        actions={can("catalog.write") && <Button asChild className="rounded-lg"><Link to="/admin/products/new" data-testid="admin-products-new-button"><Plus className="h-4 w-4 mr-2" />New product</Link></Button>}
      />

      <div className="flex flex-wrap items-center gap-1.5 mb-4" role="tablist" data-testid="admin-products-status-tabs">
        {STATUSES.map(([val, label]) => {
          const active = (params.get("status") || "") === val;
          const n = val ? counts[val] || 0 : total;
          return (
            <button key={val || "all"} role="tab" aria-selected={active} onClick={() => setParam("status", val)} data-testid={`admin-products-status-${val || "all"}`}
              className={cn("inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors", active ? "bg-foreground text-background border-foreground" : "bg-card hover:bg-secondary")}>
              {label}<span className={cn("tabular", active ? "text-background/70" : "text-muted-foreground")}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setParam("q", e.target.value); }} placeholder="Search name, brand or SKU…" className="pl-9 bg-card rounded-lg" data-testid="admin-products-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={params.get("brand_id") || "all"} onValueChange={(v) => setParam("brand_id", v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px] bg-card rounded-lg" data-testid="admin-products-brand-filter"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All brands</SelectItem>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={params.get("gender") || "all"} onValueChange={(v) => setParam("gender", v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px] bg-card rounded-lg" data-testid="admin-products-gender-filter"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All departments</SelectItem><SelectItem value="women">Women</SelectItem><SelectItem value="men">Men</SelectItem><SelectItem value="unisex">Unisex</SelectItem></SelectContent>
          </Select>
          <Select value={params.get("stock") || "all"} onValueChange={(v) => setParam("stock", v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px] bg-card rounded-lg" data-testid="admin-products-stock-filter"><SelectValue placeholder="Stock" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any stock</SelectItem><SelectItem value="in">In stock</SelectItem><SelectItem value="out">Out of stock</SelectItem></SelectContent>
          </Select>
          <Select value={params.get("sort") || "updated"} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="w-[160px] bg-card rounded-lg" data-testid="admin-products-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="updated">Recently updated</SelectItem><SelectItem value="name">Name A–Z</SelectItem><SelectItem value="price">Price low–high</SelectItem><SelectItem value="stock">Lowest stock</SelectItem></SelectContent>
          </Select>
          {hasFilters && <Button variant="ghost" size="sm" onClick={() => { setQ(""); setParams({}, { replace: true }); }} data-testid="admin-products-clear-filters"><X className="h-4 w-4 mr-1" />Clear</Button>}
        </div>
      </div>

      <div className="bg-card border rounded-xl overflow-hidden shadow-[0_1px_0_hsl(var(--shadow-1))]">
        {loading && !data ? <TableSkeleton cols={7} rows={8} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data?.items?.length ? (
          <EmptyState title={hasFilters ? "No products match" : "No products yet"} description={hasFilters ? "Adjust your filters or search for something else." : "Create your first style, add variants and imagery, then publish it to the storefront."} action={can("catalog.write") && !hasFilters && <Button asChild><Link to="/admin/products/new">Create product</Link></Button>} />
        ) : (
          <Table data-testid="admin-data-table">
            <TableHeader className="bg-secondary/60">
              <TableRow>
                <TableHead className={thCls}>Product</TableHead>
                <TableHead className={thCls}>Category</TableHead>
                <TableHead className={`${thCls} text-right`}>Variants</TableHead>
                <TableHead className={`${thCls} text-right`}>Price</TableHead>
                <TableHead className={thCls}>Stock</TableHead>
                <TableHead className={thCls}>Status</TableHead>
                <TableHead className={thCls}>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <TableRow key={p.id} className={`${rowCls} cursor-pointer`} onClick={() => navigate(`/admin/products/${p.id}`)} data-testid="admin-product-row">
                  <TableCell className={tdCls}>
                    <div className="flex items-center gap-3 min-w-[240px]">
                      <Thumb src={media(p.primary_image, 120)} alt={p.name} />
                      <div className="min-w-0">
                        <p className="font-medium truncate flex items-center gap-2" data-testid="admin-product-row-name">{p.name}{p.is_demo && <DemoBadge />}</p>
                        <p className="text-xs text-muted-foreground truncate">{p.brand_name || "—"} · {titleCase(p.gender)}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground`}>{p.category_name || "—"}</TableCell>
                  <TableCell className={`${tdCls} text-right tabular`}>{p.variant_count ?? 0}</TableCell>
                  <TableCell className={`${tdCls} text-right tabular font-medium whitespace-nowrap`}>
                    {p.min_price ? (p.max_price && p.max_price !== p.min_price ? `${money(p.min_price)} – ${money(p.max_price)}` : money(p.min_price)) : <span className="text-muted-foreground">—</span>}
                    {p.on_sale && <span className="block text-[10px] uppercase tracking-wider text-[hsl(var(--accent-ochre))]">On sale</span>}
                  </TableCell>
                  <TableCell className={tdCls}><StockPill available={p.available_total} /></TableCell>
                  <TableCell className={tdCls}><StatusBadge status={p.status} /></TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground whitespace-nowrap`}>{dateTime(p.updated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {data && <div className="mt-3"><Pager page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} onPage={(p) => setParam("page", String(p))} /></div>}
    </div>
  );
}
